<template>
  <div class="header">
    <header
      class="w-100 pb15"
      :class="{ 'checkout-header pt20' : isCheckoutPage, 'c-bg-black' : !isCheckoutPage, 'hidden-mobile': isProductPage }"
    >
      <information-header
        v-if="showInfo"
        class="brdr-bottom-1"
      />
      <div
        class="container pt15"
        :class="{ px20 : isCheckoutPage }"
      >
        <div
          v-if="!isCheckoutPage || isThankYouPage"
          class="row flex between-lg relative"
        >
          <div
            class="start-md inline-flex relative"
            :class="{'hidden-mobile': isProductPage }"
          >
            <logo
              width="auto"
              height="auto"
              :class="{'justify-center': isThankYouPage}"
              class="store-logo"
            />
            <ul class="p0 m0 relative c-white inline-flex hidden-xs hidden-tablet main-menu">
              <div>
                <router-link
                  class="block c-white cl-accent no-underline main-category"
                  :to="categoryLink(getAllCategory)"
                >
                  {{ getAllCategory.name }}
                </router-link>
              </div>
              <header-dropdown
                v-for="(menu, index) in menuItems"
                :key="index"
                class="hidden-xs"
                :class="{ 'has-search': $route.name !== 'home' && $route.name !== 'page-not-found' }"
                :rows="menu.rows"
                :list="menu.list"
                :category="menu.category"
                :open="activeMenu === index"
                @open="openMenu(index)"
                @close="scheduleMenuClose"
              />
            </ul>
          </div>
          <div
            class="end-xs hidden-xs hidden-tablet"
            :class="{'hidden': searchFocus && $route.name !== 'home'}"
          >
            <div class="inline-flex right-icons">
              <account-icon class="p15 icon pointer c-white mt5" />
            </div>
          </div>

          <div
            class="col-xs-2 hidden-desktop absolute"
            :class="[isThankYouPage ? 'hamburger-menu-success' : 'hamburger-menu']"
          >
            <div>
              <hamburger-icon class="icon pointer" />
            </div>
          </div>
          <h1
            v-if="isThankYouPage"
            class="col-xs-12 mt15 mt40-desktop pt10 pt0-desktop pt25-mobile mb0 c-white align-center"
          >
            {{ $t('Order Confirmation') }}
          </h1>
        </div>
        <div
          v-if="isCheckoutPage && !isThankYouPage"
          class="row between-xs middle-xs relative"
        >
          <div class="back-button col-xs-2 col-md-3 middle-xs absolute left-0 mt4 ml5 p0">
            <div
              class="c-white links pointer"
              @click="$router.back()"
            >
              <i class="icon icon-arrow-back" />
            </div>
          </div>
          <div class="col-xs-12 col-md-12 center-xs">
            <logo
              v-show="isThankYouPage"
              width="auto"
              height="41px"
            />
            <h4
              v-if="isCheckoutPage"
              class="c-white fs25 lh30 m0 pt0"
            >
              {{ $t('Checkout') }}
            </h4>
          </div>
        </div>
      </div>
    </header>
    <script
      type="application/ld+json"
      v-html="getJsonLd"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import CurrentPage from 'theme/mixins/currentPage'
import Menu from 'theme/mixins/menu'
import AccountIcon from 'theme/components/core/blocks/Header/AccountIcon'
import HamburgerIcon from 'theme/components/core/blocks/Header/HamburgerIcon'
import Logo from 'theme/components/core/Logo'
import { formatCategoryLink } from '@vue-storefront/core/modules/url/helpers'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import InformationHeader from 'theme/components/theme/blocks/InformationHeader.vue'
import HeaderDropdown from 'theme/components/theme/blocks/HeaderDropdown'

const MENU_CLOSE_DELAY = 200

export default {
  name: 'Header',
  components: {
    InformationHeader,
    AccountIcon,
    HamburgerIcon,
    Logo,
    HeaderDropdown
  },
  mixins: [CurrentPage, Menu],
  data () {
    return {
      isFirstVisit: false,
      hasGadSource: false,
      activeMenu: null,
      menuCloseTimer: null
    }
  },
  computed: {
    ...mapGetters({
      getCategories: 'category-next/getMenuCategories',
      getCurrentCategory: 'category-next/getCurrentCategory',
      getAvailableFilters: 'category-next/getAvailableFilters',
      getScrollPosition: 'scroll-handler/getScrollPosition',
      productsInCart: 'cart/getCartItems'
    }),
    ...mapState({
      isOpenLogin: state => state.ui.signUp,
      currentUser: state => state.user.current,
      searchFocus: state => state.ui.searchSuggestions
    }),
    isThankYouPage () {
      return this.$store.state.checkout.isThankYouPage
        ? this.$store.state.checkout.isThankYouPage
        : false
    },
    isBlackBackground () {
      return this.isCheckoutPage || this.isThankYouPage || this.isAccountPage || this.$route.path.startsWith('/i/')
    },
    isArtistPage () {
      return this.getCurrentCategory.level === 5
    },
    logoUrl () {
      const seo = currentStoreView().seo
      const url = seo.url.endsWith('/') ? seo.url.slice(0, -1) : seo.url

      return url + seo.logo
    },
    imageUrl () {
      const seo = currentStoreView().seo
      const url = seo.url.endsWith('/') ? seo.url.slice(0, -1) : seo.url

      return url + seo.image
    },
    getJsonLd () {
      return `{
        "@context": "https://schema.org/",
        "@graph":
        [
          {
            "@type": "Store",
            "name": "${currentStoreView().seo.defaultTitle}",
            "url": "${currentStoreView().seo.url}",
            "image": {
              "@type": "ImageObject",
              "url": "${this.imageUrl}"
             },
             "logo": {
              "@type": "ImageObject",
              "url": "${this.logoUrl}"
              },
             "email": "${currentStoreView().seo.email}",
             "telephone": "${currentStoreView().seo.telephone}",
             "address": {
              "@type": "${currentStoreView().seo.address.type}",
              "streetAddress": "${currentStoreView().seo.address.streetAddress}",
              "addressLocality": "${currentStoreView().seo.address.addressLocality}",
              "postalCode": "${currentStoreView().seo.address.postalCode}",
              "addressCountry": "${currentStoreView().seo.address.addressCountry}"
             }
          },
          {
            "@type": "Corporation",
            "name": "${currentStoreView().seo.defaultTitle}",
            "url": "${currentStoreView().seo.url}",
            "image": {
              "@type": "ImageObject",
              "url": "${currentStoreView().seo.url}${currentStoreView().seo.logo}"
             },
            "email": "${currentStoreView().seo.email}",
            "telephone": "${currentStoreView().seo.telephone}",
            "address": {
              "@type": "${currentStoreView().seo.address.type}",
              "streetAddress": "${currentStoreView().seo.address.streetAddress}",
              "addressLocality": "${currentStoreView().seo.address.addressLocality}",
              "postalCode": "${currentStoreView().seo.address.postalCode}",
              "addressCountry": "${currentStoreView().seo.address.addressCountry}"
            }
          }
        ]
      }`
    },
    showInfo () {
      return this.isFirstVisit || this.hasGadSource
    }
  },
  watch: {
    $route: {
      handler () {
        this.loadScrollData()
        this.checkFirstVisit()
        this.checkGadSource()
        this.closeMenu()

        document.activeElement?.blur && document.activeElement.blur()
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.menuCloseTimer)
    document.body.removeEventListener('click', () => { this.handleScrollData() })
  },
  mounted () {
    this.loadScrollData()
    this.checkFirstVisit()
    this.checkGadSource()
    document.body.addEventListener('click', () => { this.handleScrollData() })
  },
  methods: {
    openMenu (index) {
      clearTimeout(this.menuCloseTimer)
      this.activeMenu = index
    },
    scheduleMenuClose () {
      clearTimeout(this.menuCloseTimer)
      this.menuCloseTimer = setTimeout(() => { this.activeMenu = null }, MENU_CLOSE_DELAY)
    },
    closeMenu () {
      clearTimeout(this.menuCloseTimer)
      this.activeMenu = null
    },
    checkFirstVisit () {
      const hasVisited = localStorage.getItem('hasVisited')

      if (!hasVisited) {
        localStorage.setItem('hasVisited', 'true')
        this.isFirstVisit = true
      } else {
        this.isFirstVisit = false
      }
    },
    checkGadSource () {
      this.hasGadSource = !!this.$route.query.gad_source
    },
    async loadScrollData () {
      await this.$store.dispatch('scroll-handler/loadScrollHistory', { url: this.$route.path })
      if (this.getScrollPosition > 0) {
        window.scrollTo(0, this.getScrollPosition)
        await this.$store.dispatch('scroll-handler/resetScrollPosition')
      }
    },
    handleScrollData () {
      let position = window.pageYOffset || 0
      this.$store.dispatch('scroll-handler/saveScrollRecord', { url: this.$route.path, position })
    },
    gotoAccount () {
      this.$bus.$emit('modal-toggle', 'modal-signup')
    },
    categoryLink (category) {
      return formatCategoryLink(category)
    }

  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import "~theme/css/variables/grid";

.back-button,
i.icon-arrow-back {
  width: 20px;
  height: 17px;
}

header {
  z-index: 4;

  .height56 {
    height: 56px;
  }

  .container.pt15 {
    padding-left: 35px;
    padding-right: 35px;
  }

  @include desktop {
    .container {
      padding-left: 35px;
      padding-right: 35px;
    }

    &.pt15 {
      padding-top: 20px;
    }

    &.pb15 {
      padding-bottom: 20px;
    }

    &.checkout-header {
      .container {
        padding-left: 25px;
      }

      h4 {
        font-size: 48px;
        padding-top: 11px;
        padding-bottom: 23px;
      }

      .back-button {
        margin-top: 0;
        margin-left: 35px;
      }

      .back-button,
      i.icon-arrow-back {
        width: 40px;
        height: 30px;
      }
    }
  }

  @include largeDesktop {
    .container {
      padding-left: 110px;
      padding-right: 25px;
    }
  }
}

@include mediaQuery(md, 1350px) {
  .store-logo {
    /deep/ img {
      padding-right: 10px !important;
    }
  }

  .has-search {
    /deep/ button {
      padding: 10px 5px !important;
    }
  }

  .main-category {
    padding: 10px 15px !important;
  }
}

.icon {
  &:hover,
  &:focus {
    border-color: color(white);
    color: color(primary);
    opacity: 1;
  }
}

.right-icons {
  //for edge
  float: right;
}

.header-placeholder {
  height: 100px;
}

.links {
  text-decoration: underline;
}

ul.main-menu {
  list-style: none;

  @include tablet {
    margin-left: 15px;
  }

  div {
    a {
      padding: 10px 20px;
      font-size: 22px;
      line-height: 27px;

      &.router-link-exact-active {
        color: color(white);
      }
    }

    @include desktop {
      &.px9 {
        padding-left: 21px;
        padding-right: 21px;
      }
    }
  }
}

.hamburger-menu {
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;

  /deep/ button.icon {
    .icon {
      width: 20px;
      height: 15px;
    }
  }
}

.hamburger-menu-success {
  right: 0;
  top: 12px;
  padding: 0;

  /deep/ button.icon {
    .icon {
      width: 20px;
      height: 15px;
    }
  }
}

.search-bar {
  @include mediaQuery(null, 1175px) {
    display: none;
  }
}

@include mobile {
  .pt25-mobile {
    padding-top: 25px;
  }
}

.brdr-bottom-1 {
  border-color: #626262;
}

</style>
